<template>
  <div>
    <FilterOrganization :disable="false" @option="onOptionChange" :option="option"/>
    <CAlert v-if="errorMessage" color="danger" show>
      {{ errorMessage }}
    </CAlert>
    <Method_IDP class="mb-4" :item="methodItems" :year="option.year" :loading="sectionLoading.method"/>
    <ReportCompetencyIDP
      class="mb-4"
      :item="statusItems"
      :year="option.year"
      :loading="sectionLoading.status"
      caption="สรุปผลประเมินแยกรายสมรรถนะ"
    />
    <PlanSummary
      class="mb-4"
      :items="planItems"
      :overview="planOverview"
      :year="option.year"
      :loading="sectionLoading.plans"
    />
    <History
      icon="cil-speedometer"
      caption="สถานะการประเมินแยกรายบุคคล"
      :item="membersData"
      :year="option.year"
      :loading="sectionLoading.members"
    />
  </div>
</template>

<script>
import Service from "@/service/api";
import {mapGetters} from "vuex";
import FilterOrganization from "@/projects/components/Filter/FilterOrganization";
import History from "@/projects/views/E-Performances/Reports/components/Historys.vue";
import Method_IDP from "@/projects/views/E-Performances/Reports/components/Methods.vue";
import PlanSummary from "@/projects/views/E-Performances/Reports/components/PlanSummary.vue";
import ReportCompetencyIDP from "@/projects/views/E-Performances/Reports/components/ReportCompetencyIDP.vue";

const REPORT_SECTION_TIMEOUT = 45000;

function createEmptyPlanSummary() {
  return {
    overview: {
      people: 0,
      items: 0,
      levels: 0,
      completed: 0,
      pending: 0
    },
    items: []
  };
}

export default {
  name: "RIDP",
  components: {PlanSummary, ReportCompetencyIDP, Method_IDP, History, FilterOrganization},

  props: {},
  data: function () {
    return {
      option: {
        organization: "66cbe802a41ed9362a8579fa",
        agency: 0,
        year: new Date().getFullYear() + 543,
      },
      loading: true,
      errorMessage: "",
      sectionLoading: {
        method: true,
        status: true,
        members: true,
        plans: true,
      },
      sectionErrors: {},
      requestVersion: 0,
      loadDebounceId: null,
      activeAbortController: null,
      isReady: false,
      methodData: [],
      statusData: [],
      membersData: [],
      planSummaryData: createEmptyPlanSummary(),
    }

  },

  mounted() {

  },

  created() {
    this.onInit()

  },
  beforeDestroy() {
    clearTimeout(this.loadDebounceId);
    this.cancelActiveRequests();
  },

  methods: {
    async onInit() {
      await this.syncLatestReportYear();
      await this.onLoad();
      this.isReady = true;
    },

    scheduleLoad() {
      clearTimeout(this.loadDebounceId);
      this.loadDebounceId = setTimeout(() => {
        this.onLoad();
      }, 250);
    },

    async syncLatestReportYear() {
      try {
        const response = await Service.estimatesAssessment("report-years", {}, {timeout: 15000});
        const years = Array.isArray(response?.data?.data) ? response.data.data : [];
        const latestYear = parseInt(years[0], 10);

        if (Number.isFinite(latestYear)) {
          this.option.year = latestYear;
        }
      } catch (error) {
        console.error("[RIDP] failed to load report years", error?.message || error);
      }
    },

    createRequestBody() {
      const profile = this.profile || {};
      const body = {
        year: parseInt(this.option.year, 10),
        agency: this.option.agency,
        lang: this.lang
      };

      if (!Number.isFinite(body.year)) {
        body.year = this.option.year;
      }

      if (profile.role !== 1 && profile.agency && profile.agency._id) {
        body.agency = profile.agency._id;
      }

      return body;
    },

    async onLoad() {
      const requestVersion = ++this.requestVersion;
      this.cancelActiveRequests();

      const abortController = new AbortController();
      this.activeAbortController = abortController;

      this.loading = true;
      this.errorMessage = "";
      this.sectionErrors = {};

      const body = this.createRequestBody();
      const requestConfig = {
        signal: abortController.signal,
        timeout: REPORT_SECTION_TIMEOUT
      };

      await Promise.allSettled([
        this.loadSection(
          requestVersion,
          "method",
          () => Service.Report_Method("report_method", body, requestConfig),
          this.assignMethodData,
          "Failed to load method summary"
        ),
        this.loadSection(
          requestVersion,
          "status",
          () => Service.idpReportCompetency("status", body, requestConfig),
          this.assignStatusData,
          "Failed to load competency summary"
        ),
        this.loadSection(
          requestVersion,
          "members",
          () => Service.idpReportCompetency("explore", body, requestConfig),
          this.assignMembersData,
          "Failed to load member history"
        ),
        this.loadSection(
          requestVersion,
          "plans",
          () => Service.idpReportCompetency("plans-summary", body, requestConfig),
          this.assignPlanSummaryData,
          "Failed to load development plans"
        )
      ]);

      if (requestVersion === this.requestVersion) {
        this.loading = false;
        this.activeAbortController = null;
        this.updateErrorMessage();
      }
    },

    onOptionChange(e) {
      this.option = {
        ...e,
        year: parseInt(e.year, 10) || e.year
      };
      this.scheduleLoad();
    },
    async loadSection(requestVersion, key, requestFn, assignData, fallbackMessage) {
      this.$set(this.sectionLoading, key, true);
      this.$delete(this.sectionErrors, key);
      this.resetSectionData(key);

      try {
        const response = await requestFn();
        if (requestVersion !== this.requestVersion) {
          return;
        }

        assignData.call(this, response?.data?.data);
      } catch (error) {
        if (this.isCanceledError(error)) {
          return;
        }

        if (requestVersion === this.requestVersion) {
          this.$set(
            this.sectionErrors,
            key,
            error && error.message ? error.message : fallbackMessage
          );
        }
      } finally {
        if (requestVersion === this.requestVersion) {
          this.$set(this.sectionLoading, key, false);
        }
      }
    },
    cancelActiveRequests() {
      if (this.activeAbortController) {
        this.activeAbortController.abort();
        this.activeAbortController = null;
      }
    },
    isCanceledError(error) {
      return error?.code === "ERR_CANCELED" || error?.name === "CanceledError";
    },
    resetSectionData(key) {
      switch (key) {
        case "method":
          this.methodData = [];
          break;
        case "status":
          this.statusData = [];
          break;
        case "members":
          this.membersData = [];
          break;
        case "plans":
          this.planSummaryData = createEmptyPlanSummary();
          break;
        default:
          break;
      }
    },
    assignMethodData(data) {
      this.methodData = Array.isArray(data) ? data : [];
    },
    assignStatusData(data) {
      this.statusData = Array.isArray(data?.[0]?.item) ? data[0].item : [];
    },
    assignMembersData(data) {
      this.membersData = Array.isArray(data) ? data : [];
    },
    assignPlanSummaryData(data) {
      const summary = data && typeof data === "object" && !Array.isArray(data) ? data : {};

      this.planSummaryData = {
        overview: {
          ...createEmptyPlanSummary().overview,
          ...(summary.overview || {})
        },
        items: Array.isArray(summary.items) ? summary.items : []
      };
    },
    updateErrorMessage() {
      const messages = Object.values(this.sectionErrors).filter(Boolean);
      this.errorMessage = messages.length ? messages.join(" | ") : "";
    },
    resolveLocalizedValue(items) {
      if (!Array.isArray(items) || items.length === 0) {
        return "";
      }

      return (
        (items.find((entry) => entry && entry.key === this.lang) || {}).value ||
        (items[0] || {}).value ||
        ""
      );
    },

    buildPersonName(userinfo) {
      if (!userinfo) {
        return "";
      }

      return [
        this.resolveLocalizedValue(userinfo.prefix),
        this.resolveLocalizedValue(userinfo.firstName),
        this.resolveLocalizedValue(userinfo.lastName)
      ].filter(Boolean).join(" ");
    }
  },

  computed: {
    ...mapGetters({
      lang: "setting/lang",
      profile: "personal/profile",
    }),

    methodItems() {
      return this.methodData
        .map((item) => ({
          title: this.resolveLocalizedValue(item.title),
          value: item.value || 0,
        }))
        .sort((a, b) => b.value - a.value);
    },

    statusItems() {
      return this.statusData.map((item) => {
        const historys = Array.isArray(item.historys) ? item.historys : [];

        return {
          title: this.resolveLocalizedValue(item.title),
          value: item.value || 0,
          total: item.total || 0,
          history: historys.map((history) => ({
            ...history,
            accounts: this.buildPersonName((((history || {}).accounts || {}).accounts || {}).userinfo),
            assessor: this.buildPersonName(((((history || {}).assessor || {}).accounts || {}).accounts || {}).userinfo),
            state: this.resolveLocalizedValue(((((history || {}).status || []).at(-1) || {}).state || {}).title),
            status: (((history || {}).status || []).at(-1) || {}).state || {}
          })),
          pending: historys.filter((history) => ((((history || {}).status || []).at(-1) || {}).state || {})._id?.toString?.() === "654851a37ab25be71bbeeed6").length,
          completed: historys.filter((history) => ((((history || {}).status || []).at(-1) || {}).state || {})._id?.toString?.() === "67bfc7ed24e13ba338b58e4d").length,
          progress: historys.filter((history) => {
            const statusId = ((((history || {}).status || []).at(-1) || {}).state || {})._id?.toString?.();
            return statusId && statusId !== "654851a37ab25be71bbeeed6" && statusId !== "67bfc7ed24e13ba338b58e4d";
          }).length
        };
      });
    },

    planOverview() {
      return this.planSummaryData.overview;
    },

    planItems() {
      return this.planSummaryData.items;
    }
  },

  watch: {
    lang() {
      if (this.isReady) {
        this.scheduleLoad();
      }
    }
  },

}
</script>

<style scoped>

</style>
